'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRouter } from 'next/navigation';
import { Eye, EyeOff } from 'lucide-react';

interface LoginFormValues {
  password: string;
}

const SITE_PASSWORDS = ['Dartmouth26'];

export default function LoginCard() {
  const router = useRouter();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<LoginFormValues>({
    defaultValues: { password: '' },
  });

  const onSubmit = async (data: LoginFormValues) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 600));

    if (!SITE_PASSWORDS.includes(data.password)) {
      setIsLoading(false);
      setError('password', { message: 'Incorrect password — please try again' });
      return;
    }

    // Store access in sessionStorage so the gate stays unlocked
    sessionStorage.setItem('wedding_access', 'granted');
    router.push('/wedding-day-schedule');
  };

  return (
    <div className="bg-buttermilk/95 backdrop-blur-md border border-border p-10 md:p-12">
      {/* Header */}
      <div className="text-center mb-10">
        <p className="font-serif text-4xl tracking-[0.15em] text-faded-navy mb-3">G & B</p>
        <p className="text-xs tracking-[0.35em] uppercase font-sans text-stone mb-6">
          By Invitation Only
        </p>
        <div className="h-px bg-border w-12 mx-auto" />
      </div>

      <p className="font-sans text-sm text-stone text-center mb-10 leading-relaxed">
        This website is private and reserved for our invited guests. Please enter the password shared with you to access the details of our wedding weekend.
      </p>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Password */}
        <div>
          <label className="block text-xs tracking-[0.2em] uppercase font-sans text-stone mb-3">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="••••••••••"
              className="input-field pr-10"
              {...register('password', { required: 'Please enter the password' })}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-0 bottom-3 text-stone hover:text-faded-navy transition-colors duration-300"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          {errors.password && (
            <p className="mt-2 text-xs font-sans text-red-500">{errors.password.message}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full py-4 text-xs tracking-[0.3em] uppercase font-sans bg-faded-navy text-buttermilk hover:bg-deep-navy transition-all duration-400 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-3"
          style={{ minHeight: '52px' }}
        >
          {isLoading ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Entering
            </>
          ) : (
            'Enter'
          )}
        </button>
      </form>
    </div>
  );
}